import { Injectable } from '@angular/core';
import { Sample } from 'src/app/models/sample.model';
import { SampleCategoryEnum } from 'src/app/enums/sampleCategory.enum';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { ValueConversionService } from 'src/app/services/value-conversion.service';
import { LengthUnit } from 'src/app/enums/unitPreferences.enum';
import { isCustomFieldKey } from 'src/app/shared/form/custom-field-helpers';

@Injectable()
export class SampleDataConverterService {

  constructor(
    private dateFormatService: DateFormatService,
    private valueConversionService: ValueConversionService
  ) { }

  /**
   * Convert model data before saving (user format -> API format)
   */
  convertBeforeSave(model: Sample, entity: Sample): void {
    model.isFinished = true;
    model.isOutlier = false;

    // Handle dates
    this.convertDates(model, entity);

    // Handle time values
    this.convertTimes(model);

    // Handle PPE assignments
    this.convertPpeAssignments(model);

    // Handle blank sample special case
    if (model.sampleCategory === SampleCategoryEnum.Blank && model.startDate != null) {
      model.endDate = model.startDate;
    }

    // Handle sample number
    if (model.sampleNumber === 'Autogenerated') {
      model.sampleNumber = null;
    }

    // Handle wipe dimensions
    this.convertWipeDimensions(model);
  }

  /**
   * Convert dates from user format to UTC
   */
  private convertDates(model: Sample, entity: Sample): void {
    if (!model.startDate) {
      model.dateCreated = undefined;
      model.startDate = undefined;
      model.endDate = undefined;
      return;
    }

    const startLocal = this.dateFormatService.convertStringToDate(model.startDate as string);
    const datePart = this.dateFormatService.formatDateTimeToString(startLocal, this.dateFormatService.canonicalDateFormat);
    const startHHmm = this.dateFormatService.formatDateTimeToString(startLocal, this.dateFormatService.canonicalTimeFormat);
    model.startDate = this.dateFormatService.localDateTimeToUTC(datePart, startHHmm);

    const endHHmm = this.dateFormatService.parseUserTimeToHM((model.endDate || '').toString());
    if (endHHmm) {
      const overnight = endHHmm < startHHmm;
      const endDatePart = overnight ? this.dateFormatService.addDays(datePart, 1) : datePart;
      model.endDate = this.dateFormatService.localDateTimeToUTC(endDatePart, endHHmm);
    } else {
      model.endDate = undefined;
    }

    model.dateCreated = this.dateFormatService.localDateTimeToUTC(datePart, startHHmm);
  }

  /**
   * Convert shift and weekly hour values
   */
  private convertTimes(model: Sample): void {
    if (model.shiftLength !== undefined) {
      // The API stores whole seconds; 8.2 h is 29519.999... s in floating point.
      model.shiftLength = Math.round(this.valueConversionService.convertHoursToSeconds(model.shiftLength));
    }
    if (model.averageWeeklyHours_Seconds !== undefined) {
      model.averageWeeklyHours_Seconds = this.valueConversionService.convertHoursToSeconds(model.averageWeeklyHours_Seconds);
    }
  }

  /**
   * Convert PPE assignments
   */
  private convertPpeAssignments(model: Sample): void {
    if (model.ppeAssignmentList?.length) {
      model.ppeAssignments = model.ppeAssignmentList.map(ppeId => {
        const existing = model.ppeAssignments?.find(x => x.ppeId === ppeId);
        return { id: existing?.id, ppeId };
      });
    }
  }

  /**
   * Convert wipe dimensions from user format to storage format
   */
  private convertWipeDimensions(model: Sample): void {
    const dimensions: Record<string, number> = {};
    const hasBoth = model['length'] && model['width'];

    for (const key of ['length', 'width', 'area']) {
      let val = model[key];
      if (!val) continue;

      if (key === 'area' && hasBoth) continue;
      if (model['lengthUnit'] === LengthUnit.ft) {
        const converted = key === 'area'
          ? this.valueConversionService.convertSqFtToSqCm(val)
          : this.valueConversionService.convertFtToCm(val);
        val = Math.round(converted * 10000) / 10000;
        model[key] = val;
      }
      dimensions[key] = val;
    }

    if (Object.keys(dimensions).length) {
      model.wipeDimensions = JSON.stringify(dimensions);
    }

    // Clean up temporary properties
    delete model['length'];
    delete model['width'];
    delete model['area'];
    delete model['lengthUnit'];
  }

}
